import React, { useState, useRef, useLayoutEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import "./Sidebar.scss";
import {
  parsePermissions,
  hasPermission,
  getAllPermissionKeys,
  isSystemAdministratorRole,
  isSessionSuperAdmin,
  getSessionRoleName,
} from "../../utils/permissionUtils";
import { canAccessCapaMyWork } from "../Pages/capa/capaAccess";
import {
  getQmsPermissionKeys,
  canAccessQmsSharedWithMe,
  isQmsOrgDefaultScreen,
} from "../Pages/qms/qmsPermissionUtils";
import { organizationHasQmsModule, organizationHasSponsorCroManagementFeature } from "../../utils/orgModuleAccess";
import { getQmsSidebarSubItems } from "../Pages/qms/qmsScreenConfig";
import { useUserData } from "../../hooks/useUserData";
import useInboxUnreadCount from "../../hooks/useInboxUnreadCount";
import {
  filterSidebarMenuByOrgModules,
  subItemAllowedForOrgWithMenu,
  SIDEBAR_ADMIN_MENU_TITLE,
  SIDEBAR_ORG_MODULES,
} from "../../utils/sidebarOrgModuleFilter";
import {
  filterSidebarMenuByGrantedModules,
  isSponsorCroSession,
} from "../../utils/sponsorCroAccess";

const O = SIDEBAR_ORG_MODULES;

/** Short labels under icons when the sidebar is collapsed. */
const MENU_SHORT_LABELS = {
  Admin: "Admin",
  "Study & User Management": "Study",
  "Participant Manager": "PM",
  eISF: "eISF",
  ePSF: "ePSF",
  CAPA: "CAPA",
  "e-Source": "eSource",
  eSource: "eSource",
  QMS: "QMS",
  "E-Planner": "Planner",
  Notifications: "Inbox",
};

function getMenuShortLabel(title) {
  return MENU_SHORT_LABELS[title] || title;
}

function normalizeSidebarPath(path) {
  if (!path) return "";
  const bare = String(path).split("?")[0].split("#")[0].toLowerCase();
  if (bare.length > 1 && bare.endsWith("/")) return bare.slice(0, -1);
  return bare;
}

/** True when the current URL belongs to this menu entry (any child path). */
function isMenuRouteActive(menu, pathname) {
  const current = normalizeSidebarPath(pathname);
  if (!current) return false;
  return (menu.subItems || []).some((sub) =>
    isSubItemRouteActive(sub.path, current),
  );
}

function isSubItemRouteActive(itemPath, pathname) {
  const current = normalizeSidebarPath(pathname);
  const target = normalizeSidebarPath(itemPath);
  if (!current || !target) return false;
  return current === target || current.startsWith(`${target}/`);
}

const menuItems = [
  {
    title: "Admin",
    icon: "fa-wrench",
    color: "text-success",
    subItems: [
      {
        name: "User Management",
        path: "/usermanagement",
        orgModule: O.GLOBAL,
        permissionKey: "USER_MANAGEMENT",
        icon: "fa fa-users",
      },
      {
        name: "Sponsor/CRO management",
        path: "/assign-studies-cro",
        orgModule: O.STUDY_USER,
        permissionKey: "SPONSOR_CRO_MANAGEMENT",
        requiresSponsorCroManagement: true,
        icon: "fa fa-share-alt",
      },
      {
        name: "Role Management",
        path: "/rolemanagement",
        orgModule: O.GLOBAL,
        permissionKey: "ROLE_MANAGEMENT",
        icon:"fa fa-users-cog",
      },
      {
        name: "Site Management",
        path: "/sitemanagement",
        orgModule: O.GLOBAL,
        permissionKey: "SITE_MANAGEMENT",
        icon: "fa fa-hospital",
      },
      {
        name: "Departments",
        path: "/departments",
        orgModule: O.GLOBAL,
        permissionKey: "DEPARTMENT_MANAGEMENT",
        icon: "fa fa-building",
      },
      {
        name: "Capacity Management",
        path: "/capacitymanagement",
        orgModule: O.GLOBAL,
        permissionKey: "CAPACITY_MANAGEMENT",
        icon: "fa fa-warehouse",
      },
      {
        name: "Organisation Setup",
        path: "/organizationsetup",
        orgModule: O.GLOBAL,
        permissionKey: "AdminRole",
        icon: "fa fa-building",
      },
      {
        name: "Customer Organisations",
        path: "/superadmin/organisations",
        orgModule: O.GLOBAL,
        permissionKey: "AdminRole",
        icon: "fa fa-sitemap",
        superAdminOnly: true,
      },
      {
        name: "Manage FAQs",
        path: "/superadmin/faq-management",
        orgModule: O.GLOBAL,
        permissionKey: "AdminRole",
        icon: "fa fa-question-circle",
        superAdminOnly: true,
      },
      {
        name: "CAPA Config",
        path: "/superadmin/capa-config",
        orgModule: O.CAPA,
        permissionKey: "CAPA_CONFIGURE",
        icon: "fa fa-sliders-h",
      },
      {
        name: "QMS Config",
        path: "/superadmin/qms-config",
        orgModule: O.QMS,
        permissionKey: "QMS_CONFIGURE",
        icon: "fa fa-cog",
      },
      {
        name: "eISF Config",
        path: "/superadmin/eisf-config",
        orgModule: O.ISF,
        permissionKey: "EISF_CONFIGURE",
        icon: "fa fa-folder-open",
      },
    ],
  },
  {
    title: "Study & User Management",
    orgModule: O.STUDY_USER,
    icon: "fa-gear",
    color: "text-purple",
    subItems: [
      {
        name: "Study Management",
        path: "/studymanagement",
        permissionKey: "STUDY_LIST",
        icon: "far fa-address-book",
      },
    ],
  },
  {
    title: "eISF",
    orgModule: O.ISF,
    icon: "fa-folder",
    color: "text-primary",
    subItems: [
      { name: "Dashboard", path: "/isfdashboard", permissionKeys: ["ISF_DASHBOARD", "VIEW_ISF"],icon:"fa fa-gauge" },
      {
        name: "Reports",
        path: "/isf/reports",
        permissionKey: "ISF_REPORTS",
        icon: "fa fa-chart-bar",
      },
      {
        name: "eISF/ePSF",
        path: "/isf",
        permissionKey: "VIEW_ISF",
        icon: "far fa-address-book",
      },
      {
        name: "Folder Library",
        path: "/folder-library",
        permissionKeys: [
          "FOLDER_LIBRARY",
          "VIEW_FOLDER_LIBRARY",
          "ADD_FOLDER_LIBRARY",
          "EDIT_FOLDER_LIBRARY",
          "DELETE_FOLDER_LIBRARY",
        ],
        icon: "fa fa-folder-open",
      },
      {
        name: "Milestone Library",
        path: "/milestone-library",
        permissionKeys: [
          "MILESTONE_LIBRARY",
          "VIEW_MILESTONE_LIBRARY",
          "ADD_MILESTONE",
          "EDIT_MILESTONE",
          "DELETE_MILESTONE",
          "MANAGE_MILESTONE_LIBRARY",
        ],
        icon: "fa fa-flag",
      },
      {
        name: "Milestone Plan",
        path: "/milestone-plan",
        permissionKeys: [
          "MILESTONE_PLAN",
          "VIEW_MILESTONE_PLAN",
          "ADD_MILESTONE_PLAN",
          "EDIT_MILESTONE_PLAN",
          "DELETE_MILESTONE_PLAN",
          "MANAGE_MILESTONE_PLAN",
        ],
        icon: "fa fa-layer-group",
      },
      {
        name: "Template Manager",
        path: "/isftemplate",
        permissionKeys: [
          "TEMPLATE_MANAGER",
          "VIEW_TEMPLATE",
          "ADD_TEMPLATE",
          "UPDATE_TEMPLATE",
          "DELETE_TEMPLATE",
        ],
        icon: "fa fa-list",
      },
      {
        name: "Audit Log",
        path: "/downloadauditlog",
        permissionKeys: [
          "AUDIT_LOG",
          "DOWNLOAD_AUDIT_LOG",
          "REQUEST_AUDIT_LOG",
        ],
        icon: "fa fa-download",
      },
    ],
  },
  {
    title: "ePSF",
    orgModule: O.EPSF,
    icon: "fa-briefcase-medical",
    color: "text-epsf",
    subItems: [
      {
        name: "Pharmacy workspace",
        path: "/epsf/imp/inventory",
        permissionKeys: [
          "VIEW_EPSF",
          "EPSF_DASHBOARD",
          "EPSF_INVENTORY_VIEW",
          "EPSF_RECEIVE",
          "EPSF_DISPENSE",
          "EPSF_RETURN",
          "EPSF_RECONCILE",
          "EPSF_DESTROY",
          "EPSF_RELEASE",
        ],
        icon: "fa fa-home",
      },
      {
        name: "IMP Products",
        path: "/epsf/imp/products",
        permissionKeys: ["EPSF_IMP_PRODUCT_MANAGE"],
        icon: "fa fa-capsules",
      },
      {
        name: "Treatment setup",
        path: "/epsf/imp/sponsor-setup",
        permissionKeys: ["EPSF_TREATMENT_SETUP"],
        icon: "fa fa-user-shield",
      },
      {
        name: "Kit mapping",
        path: "/epsf/imp/kit-mapping",
        permissionKeys: ["EPSF_KIT_TREATMENT_MANAGE"],
        icon: "fa fa-barcode",
      },
    ],
  },
  {
    title: "eSource",
    orgModule: O.ESOURCE,
    icon: "fa-file-signature",
    color: "text-esource",
    subItems: [
      {
        name: "Form Management",
        path: "/esource/templates",
        permissionKeys: [
          "ESOURCE_FORM_MANAGEMENT",
          "ESOURCE_DESIGN",
          "ESOURCE_AUDIT_LOG",
          "VIEW_ESOURCE",
        ],
        icon: "fa fa-list",
      },
    ],
  },
  {
    title: "CAPA",
    orgModule: O.CAPA,
    icon: "fa-clipboard-check",
    color: "text-warning",
    subItems: [
      {
        name: "Dashboard",
        path: "/capa-dashboard",
        permissionKey: "VIEW_CAPA",
        icon: "fa fa-chart-pie",
      },
      {
        name: "Create CAPA",
        path: "/capa/new",
        permissionKey: "ADD_CAPA",
        icon: "fa fa-plus",
      },
      {
        name: "CAPA list",
        path: "/capa",
        permissionKey: "VIEW_CAPA",
        icon: "fa fa-list",
      },
      {
        name: "Removed CAPA list",
        path: "/capa/removed",
        permissionKey: "VIEW_REMOVED_CAPA",
        icon: "fa fa-trash-alt",
      },
      {
        name: "My tasks",
        path: "/capa/tasks",
        requiresCapaModule: true,
        icon: "fa fa-tasks",
      },
      {
        name: "Reports",
        path: "/capa/reports",
        permissionKey: "CAPA_REPORTS",
        icon: "fa fa-file-export",
      },
      {
        name: "Audit log",
        path: "/capa/audit",
        permissionKey: "CAPA_AUDIT_LOG",
        icon: "fa fa-history",
      },
    ],
  },
  {
    title: "QMS",
    orgModule: O.QMS,
    icon: "fa-file-alt",
    color: "text-qms",
    subItems: getQmsSidebarSubItems(),
  },
  {
    title: "E-Planner",
    orgModule: O.EPLANNER,
    icon: "fa-calendar-check",
    color: "text-success",
    subItems: [
      {
        name: "View appointments",
        path: "/eplanner/appointments",
        icon: "fa fa-eye",
        permissionKey: "VIEW_APPOINTMENT_LIST",
      },
      {
        name: "Book appointment",
        path: "/eplanner/volunteers/search",
        icon: "far fa-calendar-check",
        permissionKey: "BOOK_APPOINTMENT",
      },
      {
        name: "Calendar",
        path: "/eplanner/calendar",
        icon: "far fa-calendar-alt",
        permissionKey: "VIEW_APPOINTMENT_CALENDAR",
      },
      {
        name: "Appointment changes",
        path: "/eplanner/appointments/changes",
        icon: "fa fa-outdent",
        permissionKey: "VIEW_APPOINTMENT_CHANGES",
      },
      {
        name: "Location occupancy",
        path: "/eplanner/occupancy",
        icon: "fa fa-map-marker",
        permissionKey: "VIEW_LOCATION_OCCUPANCY",
      },
      {
        name: "Analytics",
        path: "/eplanner/analytics",
        icon: "fa fa-chart-bar",
        permissionKey: "VIEW_EPLANNER_ANALYTICS",
      },
      {
        name: "Communication",
        path: "/eplanner/communication",
        icon: "fa fa-comments",
        permissionKey: "VIEW_WHATSAPP_COMMUNICATION",
      },
    ],
  },
  {
    title: "Participant Manager",
    orgModule: O.PARTICIPANT_MANAGER,
    icon: "fa-user-friends",
    color: "text-pm",
    subItems: [
      {
        name: "Participant Manager",
        path: "/participantmanager/active",
        icon: "fas fa-user",
        permissionKey: "PARTICIPANT_MANAGER",
      },
      {
        name: "Campaign Templates",
        path: "/participantmanager/campaign-templates",
        icon: "fa fa-bullhorn",
        permissionKey: "CAMPAIGN_TEMPLATES",
      },
      {
        name: "Feasibility Manager",
        path: "/participantmanager/feasibility",
        icon: "fa fa-list",
        permissionKey: "FEASIBILITY_MANAGER",
      },
      {
        name: "Send Bulk Email/SMS",
        path: "/participantmanager/bulk-email-sms",
        icon: "fas fa-envelope",
        permissionKey: "SEND_BULK_EMAIL_SMS",
      },
      {
        name: "Payment Email Template",
        path: "/participantmanager/payment-email-template",
        icon: "fas fa-file-invoice-dollar",
        systemAdminOnly: true,
      },
    ],
  },
];

const Sidebar = () => {
  const location = useLocation();
  const [activeIndex, setActiveIndex] = useState(null);
  const [collapsed] = useState(true);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const menuItemRefs = useRef({});
  const submenuRefs = useRef({});

  const toggleItem = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const { userData } = useUserData();
  const { unreadCount, badgeLabel } = useInboxUnreadCount(userData);
  // 🔐 Example: backend se aane wali permissions
  const backendPermissionString = userData?.Permission;
  const sessionRole = getSessionRoleName(userData);
  const isSuperAdmin = isSessionSuperAdmin(userData);
  const isSystemAdmin =
    isSystemAdministratorRole(sessionRole) || isSuperAdmin;

  const isExternalSession = isSponsorCroSession(userData);

  // 🔥 THIS IS THE KEY LOGIC — Sponsor/CRO never get System Admin permission expansion.
  const userPermissions =
    isSystemAdmin && !isExternalSession
      ? getAllPermissionKeys() // ✅ FULL ACCESS (internal only)
      : parsePermissions(backendPermissionString);

  // Layer 1: org licensed modules → Layer 2: user permissions
  // AQ Super Admin is platform operator — not a customer System Administrator.
  const orgFilteredMenus = isExternalSession
    ? filterSidebarMenuByGrantedModules(menuItems, userData)
    : filterSidebarMenuByOrgModules(menuItems, userData, {
        isSuperAdmin,
        isSystemAdministrator: isSystemAdministratorRole(sessionRole),
      });

  const filteredMenuItems = orgFilteredMenus
    .map((menu) => {
      const isAdminMenu = menu.title === SIDEBAR_ADMIN_MENU_TITLE;
      const filteredSubItems = menu.subItems.filter((sub) => {
        if (isExternalSession) {
          /*
           * Binder modules (eISF): GrantedModules / StaffExternalEntityAccess is enough.
           * Do NOT require internal VIEW_ISF — Sponsor login clears Permission and only
           * hydrates it from Module=epsf setup grants.
           */
          if (menu.orgModule === SIDEBAR_ORG_MODULES.ISF) {
            return true;
          }
          /* ePSF application: show children only when effective Permission CSV has the key. */
          const permissionKeys = Array.isArray(sub.permissionKeys)
            ? sub.permissionKeys
            : sub.permissionKey
              ? [sub.permissionKey]
              : [];
          if (permissionKeys.length === 0) return true;
          return permissionKeys.some((key) =>
            hasPermission(userPermissions, key),
          );
        }

        if (sub.superAdminOnly && !isSuperAdmin) return false;

        // AQ Super Admin: only platform Admin items (Customer Organisations, etc.)
        if (isSuperAdmin) {
          return isAdminMenu && !!sub.superAdminOnly;
        }

        // Org licence — hide unlicensed product items for everyone (incl. System Administrator).
        if (!subItemAllowedForOrgWithMenu(sub, menu, userData)) {
          return false;
        }

        if (
          sub.requiresSponsorCroManagement &&
          !organizationHasSponsorCroManagementFeature(userData)
        ) {
          return false;
        }

        // Explicit System Administrator-only screens (e.g. Payment Email Template).
        if (sub.systemAdminOnly) {
          return isSystemAdministratorRole(sessionRole);
        }

        // Customer System Administrator: all Admin screens within licensed modules
        // (covers legacy AdminRole items that are not in getAllPermissionKeys).
        if (isAdminMenu && isSystemAdmin) return true;

        // Other groups + System Administrator: full access within licensed modules.
        if (isSystemAdmin) return true;
        if (sub.requiresCapaModule) {
          return canAccessCapaMyWork({ userData, isSystemAdmin });
        }

        if (sub.requiresReviewOrApprove) {
          return canAccessQmsSharedWithMe(userPermissions, isSystemAdmin);
        }

        // Support both legacy `permissionKey` and new `permissionKeys` array.
        const permissionKeys = Array.isArray(sub.permissionKeys)
          ? sub.permissionKeys
          : sub.permissionKey
            ? sub.permissionKey.startsWith("QMS_")
              ? getQmsPermissionKeys(sub.permissionKey)
              : [sub.permissionKey]
            : [];

        // Admin items must always declare a permission key — never open by default.
        if (permissionKeys.length === 0) return !isAdminMenu;

        const orgHasQms = organizationHasQmsModule(userData);
        if (
          orgHasQms &&
          permissionKeys.some((key) => isQmsOrgDefaultScreen(key))
        ) {
          return true;
        }

        return permissionKeys.some((key) => hasPermission(userPermissions, key));
      });

      return {
        ...menu,
        subItems: filteredSubItems,
      };
    })
    .filter((menu) => menu.subItems.length > 0);

  const notificationCenterMenu = {
    title: "Notifications",
    icon: "fa-bell",
    color: "text-info",
    subItems: [
      {
        name: "My inbox",
        path: "/notification-center",
        icon: "fas fa-bell",
      },
    ],
  };

  const sponsorCroHomeMenu = {
    title: "Home",
    icon: "fa-home",
    color: "text-success",
    subItems: [
      {
        name: "Dashboard",
        path: "/shared-home",
        icon: "fas fa-home",
      },
    ],
  };

  const sidebarMenuEntries = isSponsorCroSession(userData)
    ? [sponsorCroHomeMenu, ...filteredMenuItems, notificationCenterMenu]
    : [...filteredMenuItems, notificationCenterMenu];

  const hoveredSubItemCount =
    hoveredIndex != null
      ? sidebarMenuEntries[hoveredIndex]?.subItems?.length ?? 0
      : 0;

  // Keep collapsed flyout submenus fully inside the viewport (long QMS list, etc.)
  useLayoutEffect(() => {
    if (!collapsed || hoveredIndex === null) return;

    const menuItem = menuItemRefs.current[hoveredIndex];
    const submenu = submenuRefs.current[hoveredIndex];
    if (!menuItem || !submenu) return;

    const viewportPadding = 8;
    const rect = menuItem.getBoundingClientRect();
    const maxHeight = window.innerHeight - viewportPadding * 2;

    submenu.style.left = `${rect.right}px`;
    submenu.style.maxHeight = `${maxHeight}px`;
    submenu.style.overflowY = "auto";

    const submenuHeight = Math.min(submenu.scrollHeight, maxHeight);
    let top = rect.top;

    if (top + submenuHeight > window.innerHeight - viewportPadding) {
      top = window.innerHeight - viewportPadding - submenuHeight;
    }
    if (top < viewportPadding) {
      top = viewportPadding;
    }

    submenu.style.top = `${top}px`;
  }, [collapsed, hoveredIndex, hoveredSubItemCount]);

  return (
    <div className={`sidebar ${collapsed ? "collapsed" : ""}`}>
      <ul className="list-unstyled">
        {sidebarMenuEntries.map((item, index) => {
          const isNotifications = item.title === "Notifications";
          const showInboxBadge = isNotifications && unreadCount > 0;
          const isRouteActive = isMenuRouteActive(item, location.pathname);
          const isExpanded = activeIndex === index;

          return (
          <li
            key={item.title}
            className={`menu-group ${item.disabled ? "disabled" : ""} ${
              item.title === "Notifications" ? "menu-group--notification-center" : ""
            }`}
            onMouseEnter={() => collapsed && setHoveredIndex(index)}
            onMouseLeave={() => collapsed && setHoveredIndex(null)}
          >
            <div
              ref={(el) => (menuItemRefs.current[index] = el)}
              className={`menu-item ${item.color} ${
                isRouteActive ? "active" : ""
              }`}
              title={
                collapsed
                  ? isNotifications && unreadCount
                    ? `${item.title} (${unreadCount} unread)`
                    : item.title
                  : undefined
              }
              onClick={() => !item.disabled && toggleItem(index)}
            >
              {collapsed ? (
                <>
                  <span className="menu-item__icon-wrap">
                    <i className={`fas ${item.icon}`} aria-hidden="true" />
                    {showInboxBadge ? (
                      <span className="sidebar-menu-badge" aria-hidden="true">
                        {badgeLabel}
                      </span>
                    ) : null}
                  </span>
                  <span className="menu-item__short-label">{getMenuShortLabel(item.title)}</span>
                </>
              ) : (
                <>
                  <span className="menu-item__icon-wrap me-2">
                    <i className={`fas ${item.icon}`} aria-hidden="true" />
                    {showInboxBadge ? (
                      <span className="sidebar-menu-badge" aria-hidden="true">
                        {badgeLabel}
                      </span>
                    ) : null}
                  </span>
                  <span className="menu-item__title">{item.title}</span>
                  {item.subItems.length > 0 && (
                    <i
                      className={`fas fa-chevron-${
                        isExpanded ? "up" : "down"
                      } ms-auto`}
                      aria-hidden="true"
                    />
                  )}
                </>
              )}
            </div>

            {/* Expanded mode submenu */}
            {!collapsed &&
              isExpanded &&
              item.subItems.length > 0 && (
                <ul className="submenu list-unstyled ps-4">
                  {item.subItems.map((sub, idx) => (
                    <li key={idx}>
                      <Link
                        to={sub.path}
                        className={`d-block text-decoration-none text-dark${
                          isSubItemRouteActive(sub.path, location.pathname)
                            ? " active"
                            : ""
                        }`}
                      >
                        <i
                          className={
                            sub.icon ? sub.icon + " me-2" : "fas fa-plus me-2"
                          }
                        ></i>
                        {sub.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}

            {/* Collapsed mode submenu on hover */}
            {collapsed &&
              item.subItems.length > 0 &&
              hoveredIndex === index && (
                <ul
                  ref={(el) => (submenuRefs.current[index] = el)}
                  className="submenu list-unstyled"
                >
                  {item.subItems.map((sub, idx) => (
                    <li key={idx}>
                      <Link
                        to={sub.path}
                        className={`d-block text-decoration-none text-dark${
                          isSubItemRouteActive(sub.path, location.pathname)
                            ? " active"
                            : ""
                        }`}
                      >
                        {sub.icon && (
                          <i className={sub.icon + " me-2"}></i>
                        )}
                        {sub.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
          </li>
          );
        })}
      </ul>
    </div>
  );
};

export default Sidebar;
